/**
 * EmployeeCabinet — per-employee profile page.
 * Route: /employees/:id
 * Shows: name, role, contact, KPI last 30 days, attendance log, ping history, total hours this month.
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

// ── Types ──────────────────────────────────────────────────────────────────────

interface AttendanceLogEntry {
  id: string;
  shift_type: string;
  log_date: string;
  actual_time: string;
  minutes_offset: number;
  within_grace: boolean;
  tactical_delay: boolean;
  device_type: string | null;
}

interface KPI {
  attendance_rate: number;
  on_time_count: number;
  late_count: number;
  early_count: number;
  total_check_ins: number;
  scheduled_hours: number;
  actual_hours_present: number;
  kpi_mode: string;
}

interface ProfileData {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string;
  is_active: boolean;
  kpi_mode: string;
  kpi: KPI;
  total_hours_this_month: number;
  attendance_logs: AttendanceLogEntry[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const SHIFT_LABELS: Record<string, string> = {
  morning_in:  "Morning In",
  morning_out: "Morning Out",
  evening_in:  "Evening In",
  evening_out: "Evening Out",
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    timeZone: "Asia/Dubai", weekday: "short", month: "short", day: "numeric",
  });
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-US", {
    timeZone: "Asia/Dubai", hour: "2-digit", minute: "2-digit",
  });
}

function kpiColor(rate: number) {
  if (rate >= 90) return "#22c55e";
  if (rate >= 70) return "#f59e0b";
  return "#ef4444";
}

function roleColor(role: string) {
  if (role === "owner" || role === "super_admin") return "#ffd6a5";
  if (role === "manager") return "#bdb2ff";
  return "#94a3b8";
}

// ── Component ─────────────────────────────────────────────────────────────────

const EmployeeCabinet: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetch(`/api/employee/${id}/profile?days=30`, { credentials: "include" })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => { setProfile(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [id]);

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.loadingBox}>
          <span style={{ fontSize: 32 }}>⏳</span>
          <p style={{ color: "#94a3b8", marginTop: 12 }}>Loading profile…</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div style={s.page}>
        <div style={s.errorBox}>
          <span style={{ fontSize: 32 }}>⚠️</span>
          <p style={{ color: "#ef4444", marginTop: 12 }}>Failed to load: {error}</p>
          <button style={s.backBtn} onClick={() => navigate(-1)}>← Back</button>
        </div>
      </div>
    );
  }

  const kpi = profile.kpi;
  const rate = kpi.attendance_rate;

  return (
    <div style={s.page}>
      {/* Back */}
      <button style={s.backBtn} onClick={() => navigate(-1)}>← Back</button>

      {/* ── Hero card ── */}
      <div style={s.heroCard}>
        <div style={s.avatar}>
          {profile.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)}
        </div>
        <div style={s.heroInfo}>
          <h1 style={s.heroName}>{profile.name}</h1>
          <span style={{ ...s.roleBadge, color: roleColor(profile.role), borderColor: roleColor(profile.role) }}>
            {profile.role.replace(/_/g, " ")}
          </span>
          {profile.kpi_mode !== "attendance" && (
            <span style={s.modeBadge}>KPI: {profile.kpi_mode} mode</span>
          )}
          <div style={s.contact}>
            {profile.email && <span>✉ {profile.email}</span>}
            {profile.phone && <span>📞 {profile.phone}</span>}
            {!profile.is_active && <span style={{ color: "#ef4444" }}>Inactive</span>}
          </div>
        </div>
      </div>

      {/* ── KPI summary ── */}
      <div style={s.kpiGrid}>
        <div style={s.kpiCard}>
          <div style={{ ...s.kpiValue, color: kpiColor(rate) }}>{rate.toFixed(1)}%</div>
          <div style={s.kpiLabel}>Attendance Rate (30d)</div>
          <div style={{ ...s.kpiBar, background: "#1e2a45", marginTop: 8, borderRadius: 4, height: 6 }}>
            <div style={{ width: `${Math.min(rate, 100)}%`, height: "100%", background: kpiColor(rate), borderRadius: 4 }} />
          </div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiValue}>{kpi.actual_hours_present.toFixed(1)}h</div>
          <div style={s.kpiLabel}>Hours Present (30d)</div>
        </div>
        <div style={s.kpiCard}>
          <div style={{ ...s.kpiValue, color: "#22c55e" }}>{kpi.on_time_count}</div>
          <div style={s.kpiLabel}>On Time</div>
        </div>
        <div style={s.kpiCard}>
          <div style={{ ...s.kpiValue, color: kpi.late_count > 0 ? "#ef4444" : "#94a3b8" }}>{kpi.late_count}</div>
          <div style={s.kpiLabel}>Late</div>
        </div>
        <div style={s.kpiCard}>
          <div style={{ ...s.kpiValue, color: "#ffd6a5" }}>{profile.total_hours_this_month.toFixed(1)}h</div>
          <div style={s.kpiLabel}>Hours This Month</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiValue}>{kpi.total_check_ins}</div>
          <div style={s.kpiLabel}>Total Check-ins (30d)</div>
        </div>
      </div>

      {/* ── Attendance log ── */}
      <div style={s.section}>
        <h2 style={s.sectionTitle}>📋 Attendance Log (last 30 days)</h2>
        {profile.attendance_logs.length === 0 ? (
          <p style={{ color: "#64748b" }}>No attendance records found.</p>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {["Date", "Shift", "Time", "Offset", "Device", "Status"].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {profile.attendance_logs.map(log => (
                  <tr key={log.id} style={{
                    background: log.tactical_delay
                      ? "rgba(239,68,68,0.05)"
                      : log.within_grace
                        ? "rgba(34,197,94,0.04)"
                        : "transparent",
                  }}>
                    <td style={s.td}>{fmtDate(log.log_date)}</td>
                    <td style={s.td}>{SHIFT_LABELS[log.shift_type] || log.shift_type}</td>
                    <td style={s.td}>{fmtTime(log.actual_time)}</td>
                    <td style={{ ...s.td, color: log.minutes_offset > 15 ? "#ef4444" : log.minutes_offset < -15 ? "#22c55e" : "#94a3b8" }}>
                      {log.minutes_offset > 0 ? "+" : ""}{log.minutes_offset.toFixed(1)}m
                    </td>
                    <td style={{ ...s.td, color: "#64748b", fontSize: 12 }}>
                      {log.device_type === "remote" ? "🌐 Remote" :
                       log.device_type === "mobile" ? "📱 Mobile" :
                       log.device_type === "tablet" ? "📟 Tablet" :
                       log.device_type === "desktop" ? "💻 Desktop" :
                       log.device_type || "—"}
                    </td>
                    <td style={s.td}>
                      {log.tactical_delay ? (
                        <span style={s.badgeRed}>Late</span>
                      ) : log.within_grace ? (
                        <span style={s.badgeGreen}>On Time</span>
                      ) : (
                        <span style={s.badgeYellow}>Borderline</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "#0b1326",
    padding: "24px 20px 60px",
    fontFamily: "Montserrat, sans-serif",
    color: "#f8fafc",
    boxSizing: "border-box",
  },
  loadingBox: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", minHeight: "60vh",
  },
  errorBox: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", minHeight: "60vh",
  },
  backBtn: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    color: "#94a3b8",
    fontFamily: "Montserrat, sans-serif",
    fontSize: 13,
    padding: "6px 14px",
    cursor: "pointer",
    marginBottom: 20,
  },
  heroCard: {
    display: "flex",
    alignItems: "center",
    gap: 24,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,214,165,0.15)",
    borderRadius: 20,
    padding: "24px 28px",
    marginBottom: 24,
    flexWrap: "wrap",
  },
  avatar: {
    width: 72, height: 72,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #ffd6a5, #bdb2ff)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 28, fontWeight: 800, color: "#0b1326",
    flexShrink: 0,
  },
  heroInfo: { display: "flex", flexDirection: "column", gap: 6 },
  heroName: { fontSize: 24, fontWeight: 800, color: "#f8fafc", margin: 0 },
  roleBadge: {
    display: "inline-block",
    border: "1px solid",
    borderRadius: 6,
    padding: "2px 10px",
    fontSize: 12,
    fontWeight: 700,
    width: "fit-content",
    textTransform: "capitalize" as const,
  },
  modeBadge: {
    display: "inline-block",
    background: "rgba(189,178,255,0.12)",
    border: "1px solid rgba(189,178,255,0.3)",
    borderRadius: 6,
    padding: "2px 10px",
    fontSize: 11,
    color: "#bdb2ff",
    width: "fit-content",
  },
  contact: { display: "flex", gap: 14, color: "#64748b", fontSize: 13, flexWrap: "wrap" as const },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 14,
    marginBottom: 24,
  },
  kpiCard: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 14,
    padding: "18px 16px",
  },
  kpiValue: { fontSize: 28, fontWeight: 800, color: "#f8fafc" },
  kpiLabel: { fontSize: 11, color: "#64748b", textTransform: "uppercase" as const, letterSpacing: "0.05em", marginTop: 4 },
  kpiBar: {},
  section: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 16,
    padding: "20px 24px",
    marginBottom: 20,
  },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: "#f8fafc", margin: "0 0 16px" },
  tableWrap: { overflowX: "auto" as const },
  table: { width: "100%", borderCollapse: "collapse" as const, fontSize: 13 },
  th: { textAlign: "left" as const, color: "#64748b", fontWeight: 600, fontSize: 11, textTransform: "uppercase" as const, letterSpacing: "0.05em", padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.07)" },
  td: { padding: "9px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", color: "#cbd5e1" },
  badgeGreen: { background: "rgba(34,197,94,0.12)", color: "#22c55e", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 600 },
  badgeRed:   { background: "rgba(239,68,68,0.12)", color: "#ef4444", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 600 },
  badgeYellow:{ background: "rgba(245,158,11,0.12)", color: "#f59e0b", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 600 },
};

export default EmployeeCabinet;
